<template>
  <div
    class="visits-overview-tile"
    :class="{
      selected: selected,
    }"
    @click="onVisitSelected"
  >
    <div class="visits-overview-tile__header">
      <div class="visits-overview-tile__title">
        <span class="visit-id" @click.stop="onClick">{{ visit.Id }}</span>
        <BadgeIndicator type="default" :text="getVisitType(visit.Type)" />
        <BadgeIndicator :class="`status-badge ${getStatusClass(visit.ReportStatus)}`" :text="visit.ReportStatus" />
        <BadgeIndicator v-if="visit.UrgentActionReq" type="important" icon="circle-exclamation" text="URGENT" />
      </div>
      <div class="visits-overview-tile__date">
        {{ moment(visit.ActualVisitDate).format("MM/DD/YYYY") }}
        <span v-if="isClosedVisit" class="closed-date">
          Closed {{ moment(visit.StatusDate).format("MM/DD/YYYY") }}
        </span>
      </div>
    </div>

    <div class="visit-header">
      <div class="visit-header__item">
        <div class="label">Plant</div>
        <div class="value">
          <a :href="`/oudirect/PlantHistory/${visit.PlantId}`" target="_blank" class="plant-name-link" type="button">{{
            visit.PlantName
          }}</a>
          <span v-if="visit.GroupPlant" class="group-plant" @click="onGroupPlantClick"> Ⓖ </span>
        </div>
      </div>
      <div class="visit-header__item">
        <div class="label">Company</div>
        <div class="value">
          {{ visit.Company }}
        </div>
      </div>
      <div class="visit-header__item">
        <div class="label">Location</div>
        <div class="value">{{ visit.Country ? visit.Country + ", " : "" }}{{ visit.City }}</div>
      </div>
      <div class="visit-header__item">
        <div class="label">RFR</div>
        <div class="value">{{ visit.RfrName }}</div>
      </div>
      <div class="visit-header__item">
        <div class="label">RC</div>
        <div class="value">{{ visit.Rc }}</div>
      </div>
      <div class="visit-header__item">
        <div class="label">Group Leader</div>
        <div class="value">{{ visit.GroupLeaderName }}</div>
      </div>
    </div>

    <!-- Schedule A Issues -->
    <div v-if="visit.ScheduleA || visit.InspectionScheduleAComment" class="visits-overview-tile__content">
      <div class="section-label">
        <div class="checkbox-container">
          <Checkbox :value="visit.ScheduleA" label="Schedule A Issues" readonly />
        </div>
      </div>
      <div v-if="visit.InspectionScheduleAComment" class="section-content">
        <VisitsOverviewComment :comment="visit.InspectionScheduleAComment" />
      </div>

      <!-- Ingredients Table -->
      <table v-if="visit.ScheduleAItems && visit.ScheduleAItems.length" class="schedule-table">
        <thead>
          <tr>
            <th v-for="(header, idx) in ingredientHeaders" :key="idx">
              {{ header }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(ingredient, i) in visit.ScheduleAItems" :key="i">
            <td>{{ ingredient.IsIncorrect ? "Incorrect" : "Missing" }}</td>
            <td>{{ ingredient.IngredientName }}</td>
            <td>{{ ingredient.Source }}</td>
            <td>{{ ingredient.CertifiedAgency }}</td>
            <td>{{ ingredient.Comments }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Schedule B Issues -->
    <div v-if="visit.ScheduleB || visit.InspectionScheduleBComment" class="visits-overview-tile__content">
      <div class="section-label">
        <div class="checkbox-container">
          <Checkbox :value="visit.ScheduleB" label="Schedule B Issues" readonly />
        </div>
      </div>
      <div v-if="visit.InspectionScheduleBComment" class="section-content">
        <VisitsOverviewComment :comment="visit.InspectionScheduleBComment" />
      </div>

      <!-- Products Table -->
      <table v-if="visit.ScheduleBItems && visit.ScheduleBItems.length" class="schedule-table">
        <thead>
          <tr>
            <th v-for="(header, i) in productHeaders" :key="i">
              {{ header }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(product, i) in visit.ScheduleBItems" :key="i">
            <td>{{ product.IsIncorrect ? "Incorrect" : "Missing" }}</td>
            <td>{{ product.BrandName }}</td>
            <td>{{ product.Label }}</td>
            <td>{{ product.Dpm }}</td>
            <td>{{ product.Comments }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-for="(comment, i) in comments" :key="i" class="visits-overview-tile__content">
      <div class="section-label">{{ comment.label }}</div>
      <div class="section-content">
        <VisitsOverviewComment :comment="comment.comment" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { EnumVisitType } from "@/Domain/EnumVisitType";
import type { VisitDisplay } from "@/Domain/VisitDisplay";
import { splitByCapitalLetter } from "@/shared/helpers/enumHelper";
import { Checkbox } from "@progress/kendo-vue-inputs";
import moment from "moment";
import VisitsOverviewComment from "@/views/inspections/VisitsOverview/VisitsOverviewComment.vue";
import BadgeIndicator from "@/shared/ui/BadgeIndicator.vue";
import type { VisitInspectionCommentDisplay } from "@/Domain/VisitInspectionCommentDisplay";
import { computed } from "vue";

interface CommentView {
  label: string;
  comment: VisitInspectionCommentDisplay;
}

const ingredientHeaders = ["Issue", "Ingredient Name", "Source", "Agency", "RMC / Comments"];

const productHeaders = ["Issue", "Brand Name", "Label", "D/P/M", "Comments"];

const props = defineProps<{
  visit: VisitDisplay;
  selected?: boolean;
}>();

const emits = defineEmits<{
  (e: "visitClicked", visitId: number): void;
  (e: "groupPlantClicked", visit: VisitDisplay): void;
  (e: "visitSelected", visit: VisitDisplay): void;
}>();

const comments = computed(() => {
  const comments: CommentView[] = [
    {
      label: "Urgent Addressed",
      comment: props.visit.InspectionUrgentMattersComment,
    },
    {
      label: "Comments",
      comment: props.visit.InspectionGeneralComment,
    },
    {
      label: "Confidential Comments",
      comment: props.visit.InspectionConfidentialComment,
    },
    {
      label: "Heat Graphs",
      comment: props.visit.HeatGraphsComment,
    },
    {
      label: "Integrity of Lines",
      comment: props.visit.IntegrityOfLinesComment,
    },
    {
      label: "CIP Records",
      comment: props.visit.CipRecordsComment,
    },
    {
      label: "Batch Sheets",
      comment: props.visit.BatchSheetsComment,
    },
    {
      label: "Formula Cards",
      comment: props.visit.FormulaCardsComment,
    },
    {
      label: "Bills of Lading",
      comment: props.visit.BillsOfLadingComment,
    },
    {
      label: "Carrier Status",
      comment: props.visit.CarrierStatusComment,
    },
    {
      label: "Trace Lines",
      comment: props.visit.TraceLinesComment,
    },
    {
      label: "Closed Comments",
      comment: props.visit.ClosedComment,
    },
    {
      label: "Reopened Comments",
      comment: props.visit.ReopenedComment,
    },
  ];

  return comments.filter((comment) => comment.comment);
});

const isClosedVisit = computed(() => {
  return props.visit.ReportStatus === "Closed";
});

const onClick = function () {
  emits("visitClicked", props.visit.Id);
};

const onGroupPlantClick = function () {
  emits("groupPlantClicked", props.visit);
};

const onVisitSelected = function (event: MouseEvent) {
  // Prevent double events when clicking on the other actions (visit redirect, group plant)
  let className = (event.target as HTMLElement).className;
  if ((className as any)?.baseVal) className = (className as any)?.baseVal;

  if (
    className?.includes &&
    (className.includes("group-plant") || className.includes("visit-icon") || className.includes("expand-link"))
  ) {
    return;
  }
  emits("visitSelected", props.visit);
};

const getVisitType = (type: number) => {
  return splitByCapitalLetter(EnumVisitType[type]);
};

const getStatusClass = (status: string) => {
  if (!status) return "status-other";

  const statusLower = status.toLowerCase();
  if (statusLower.includes("submitted")) return "status-submitted";
  if (statusLower.includes("reopened")) return "status-reopened";
  if (statusLower.includes("reviewed")) return "status-reviewed";

  return "status-other";
};
</script>

<style lang="scss">
.visits-overview-tile {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
  border-top: 4px solid #81b5ec;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    border-bottom: 1px solid #e0e0e0;
    background-color: #f0f5fb;
  }

  &__title {
    display: flex;
    align-items: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;

    .visit-id {
      font-weight: 600;
      font-size: 1rem;
      color: #3a5da6;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__date {
    color: #666;
    font-size: 0.85rem;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  &.selected {
    border: 2px solid #81b5ec;
  }

  .visit-header {
    display: flex;
    background-color: #f7f7f7;
    padding: 0;
    position: relative;
    flex-wrap: wrap;

    &__item {
      flex: 1 1 20%;
      padding: 12px;
      font-size: 0.9rem;

      @include media-breakpoint-up(lg) {
        flex: 1;
      }

      .label {
        font-weight: 600;
        color: #555;
        margin-bottom: 4px;
      }

      .value {
        color: #333;

        .plant-name-link {
          color: #3a5da6;
          text-decoration: underline;
          cursor: pointer;

          &:hover {
            color: #2a4d96;
          }
        }
      }
    }

    .visit-icon {
      position: absolute;
      top: 8px;
      right: 8px;
      cursor: pointer;
      color: #007bff;
      font-size: 1.1rem;

      &:hover {
        color: #0056b3;
      }
    }
  }

  &__content {
    background-color: #fafafa;
    padding: 12px;
    border-top: 1px solid #e0e0e0;
    flex-wrap: wrap;
    overflow-x: auto;

    .section-label {
      font-weight: 600;
      font-size: 0.9rem;
      color: #444;
    }

    .section-content {
      margin-top: 4px;
      color: #333;
    }

    .checkbox-container {
      display: flex;
      align-items: center;
      cursor: pointer;
      font-weight: 600;
      font-size: 0.9rem;
      user-select: none;
      color: #444;

      .k-checkbox-wrap {
        width: 160px;
        flex-direction: row-reverse;
        justify-content: space-between;
      }

      .k-checkbox {
        margin-left: auto !important;
      }

      .k-checkbox-label {
        text-wrap: auto;
      }
    }

    &:has(.section-label) {
      display: flex;

      .section-label {
        width: 180px;
      }

      .section-content {
        flex: 1;
        margin-top: 0;
        margin-left: 12px;
      }
    }
  }

  .schedule-table {
    width: 100%;
    border-collapse: collapse;
    background-color: #fff;
    border-radius: 4px;
    overflow: hidden;
    margin-top: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    th,
    td {
      padding: 8px 12px;
      text-align: left;
      border: 1px solid #e5e5e5;
      font-size: 0.85rem;
      color: #333;
    }

    th {
      background-color: #f5f5f5;
      font-weight: 600;
      color: #444;
    }

    tr:nth-child(even) {
      background-color: #fcfcfc;
    }

    tr:hover {
      background-color: #f8f8f8;
    }
  }

  .group-plant {
    font-size: 1.3rem;
    color: #007bff;
    font-family: "Segoe UI Symbol";
    cursor: pointer;
    line-height: 0;

    :hover {
      color: #0056b3;
    }
  }
}

.status-badge {
  font-size: 0.85rem;
  font-weight: 600;

  &.status-submitted,
  &.status-reopened {
    background-color: #ffc107;
    color: #212529;
  }

  &.status-reviewed {
    background-color: #17a2b8;
    color: white;
  }

  &.status-other {
    background-color: #6c757d;
    color: white;
  }
}

@include media-breakpoint-down(sm) {
  .visits-overview-tile {
    .visit-header {
      &__item {
        border-right: none;
        border-bottom: 1px solid #e0e0e0;
        padding: 8px;
        flex: 1 1 50%;

        &:last-child {
          border-bottom: none;
        }
      }
    }

    &__content {
      gap: 8px;

      &:has(.section-label) {
        flex-direction: column;
      }

      .section-content {
        flex: 1 1 100%;
        margin-left: 0;
        margin-top: 4px;
      }
    }

    .schedule-table {
      display: block;
      overflow-x: auto;
    }
  }
}
</style>
